import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, Store, Check, Loader2, Package, BadgeCheck, Sparkles, BookOpen, Tag } from "lucide-react";
import PublicLayout from "@/pages/public/PublicLayout";
import DiscoveryFilterBar from "@/pages/public/DiscoveryFilterBar";
import HRail from "@/pages/public/HRail";
import { usePublicCurrency } from "@/contexts/PublicCurrency";

function StorePlans() {
  const { fmt } = usePublicCurrency();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/store/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);
  if (!plans.length) return null;
  return (
    <section className="nb-card p-5 bg-[#C4B5FD]" data-testid="store-plans">
      <h2 className="display text-lg font-bold mb-3 flex items-center gap-2"><Tag size={18} /> Store Partner Plans</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plans.map(p => (
          <div key={p.plan_key} className="nb-card bg-white p-4">
            <div className="font-black display text-base">{p.name}</div>
            <div className="display text-2xl font-black mono mt-1">{fmt(p.price_monthly_inr, p.price_monthly_usd)}<span className="text-xs font-normal opacity-60">/mo</span></div>
            <div className="text-xs opacity-70 mt-1">{p.storage_gb} GB · {p.commission_pct}% commission · {p.max_skus} SKUs</div>
            <Link to="/vendor/onboard" className="nb-btn text-xs mt-3 w-full justify-center">Become a Partner</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function StoreInner() {
  const [filters, setFilters] = useState({ country: "", state: "", district: "", board: "", grade: "", subject: "" });
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [products, setProducts] = useState([]);      // current vendor catalog
  const [allProducts, setAllProducts] = useState([]); // cross-vendor rails
  const [digital, setDigital] = useState([]);
  const [cart, setCart] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cust, setCust] = useState({ customer_name: "", customer_phone: "", delivery_address: "", fulfillment: "DELIVERY" });
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/store/categories").then(r => setCats(r.data)).catch(() => {}); }, []);
  // Vendors react to geo filters.
  useEffect(() => {
    api.get("/store/vendors", { params: {
      country_code: filters.country || undefined, state: filters.state || undefined, district: filters.district || undefined,
    } }).then(r => { setVendors(r.data); if (!r.data.find(x => x.vendor_id === vendorId)) setVendorId(r.data[0]?.vendor_id || ""); }).catch(() => {});
  }, [filters.country, filters.state, filters.district]);
  // Cross-vendor product rail + digital rail react to academic filters.
  useEffect(() => {
    api.get("/store/products").then(r => setAllProducts(r.data)).catch(() => {});
    api.get("/tv/content", { params: { board: filters.board || undefined, grade: filters.grade || undefined, subject: filters.subject || undefined } })
      .then(r => setDigital((r.data || []).filter(c => c.content_type === "NOTE" || c.content_type === "COURSE"))).catch(() => {});
  }, [filters.board, filters.grade, filters.subject]);
  // Current-vendor catalog.
  useEffect(() => {
    if (!vendorId) { setProducts([]); return; }
    api.get("/store/products", { params: { vendor_id: vendorId, category_code: cat || undefined } })
      .then(r => setProducts(r.data)).catch(() => {});
  }, [vendorId, cat]);

  const gMatch = (p) => (!filters.grade || (p.grade_tag || "").includes(filters.grade)) && (!filters.subject || (p.subject_tag || "").toLowerCase() === filters.subject.toLowerCase());
  const trending = useMemo(() => allProducts.filter(gMatch).slice(0, 12), [allProducts, filters.grade, filters.subject]);

  const add = (p) => setCart({ ...cart, [p.product_id]: { product: p, qty: (cart[p.product_id]?.qty || 0) + 1 } });
  const remove = (id) => { const c = { ...cart }; delete c[id]; setCart(c); };
  const items = Object.values(cart);
  const total = useMemo(() => items.reduce((s, x) => s + x.product.selling_price * x.qty, 0), [cart]);
  const vendor = vendors.find(v => v.vendor_id === vendorId);
  const cartCount = items.reduce((s, x) => s + x.qty, 0);

  const checkout = async () => {
    if (!cust.customer_name || !cust.customer_phone) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/store/checkout", {
        vendor_id: vendorId, items: items.map(x => ({ product_id: x.product.product_id, qty: x.qty })),
        ...cust, country_code: vendor?.country_code || "IN",
      });
      setReceipt(r.data); setCart({}); setCheckoutOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <DiscoveryFilterBar value={filters} onChange={setFilters} theme="light" />
      {/* floating cart */}
      <button className="fixed bottom-5 right-5 z-40 nb-btn shadow-[3px_3px_0_0_#000]"
        onClick={() => cartCount ? setCheckoutOpen(true) : toast.info("Cart is empty")} data-testid="store-cart-btn">
        <ShoppingCart size={18} /> {cartCount > 0 && <span className="nb-badge bg-[#FDE047] ml-1">{cartCount}</span>}
      </button>

      <main className="max-w-6xl mx-auto p-4 space-y-8">
        <div className="nb-card bg-[#FDE047] p-6 md:p-8" data-testid="store-hero">
          <span className="nb-badge bg-white text-xs">SCHOOLZ STORE</span>
          <h1 className="display text-3xl md:text-5xl font-black mt-2 leading-tight">Campus bookstores, study kits<br className="hidden md:block" /> & creator notes — delivered locally.</h1>
          <p className="text-sm mt-2 max-w-xl">Textbooks, uniforms, stationery bundles and digital study material from verified stores in your district.</p>
        </div>

        <HRail title="Featured Local Stores" icon={Store} empty={!vendors.length && <div className="text-sm text-slate-500 py-4">No stores match this location yet — try a wider area.</div>}>
          {vendors.map(v => (
            <Link key={v.vendor_id} to={`/store/vendor/${v.vendor_id}`} className="min-w-[230px] snap-start nb-card bg-white p-4 hover:-translate-y-1 transition-transform" data-testid={`store-vendor-card-${v.vendor_id}`}>
              <div className="w-11 h-11 bg-[#93C5FD] border-2 border-black rounded grid place-items-center mb-2"><Store size={20} /></div>
              <div className="font-bold text-sm flex items-center gap-1">{v.business_name}{v.kyc_status === "VERIFIED" && <BadgeCheck size={15} className="text-green-700" />}</div>
              <div className="text-xs opacity-60">{v.city}{v.district ? ` · ${v.district}` : ""} · {v.postal_code}</div>
              <div className="nb-badge bg-[#FDE047] text-[10px] mt-2 inline-block">Visit store →</div>
            </Link>
          ))}
        </HRail>

        <HRail title="Trending Bundles & Kits" icon={Sparkles} empty={!trending.length && <div className="text-sm text-slate-500 py-4">No matching products yet.</div>}>
          {trending.map(p => (
            <Link key={p.product_id} to={`/store/vendor/${p.vendor_id}`} className="min-w-[190px] snap-start nb-card bg-white p-3 hover:-translate-y-1 transition-transform" data-testid={`store-trending-${p.product_id}`}>
              <div className="h-24 bg-[#FAF7F0] border-2 border-black rounded grid place-items-center mb-2 overflow-hidden">
                {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <Package size={26} className="opacity-40" />}
              </div>
              <div className="font-bold text-sm line-clamp-2">{p.title}</div>
              <div className="text-xs opacity-60">{p.category_code}{p.grade_tag && ` · ${p.grade_tag}`}</div>
              <div className="display text-base font-black mono mt-1">₹{p.selling_price}{p.mrp > p.selling_price && <span className="text-xs line-through opacity-50 ml-1">₹{p.mrp}</span>}</div>
            </Link>
          ))}
        </HRail>

        <HRail title="Bestselling Digital Courses & Notes" icon={BookOpen} empty={!digital.length && <div className="text-sm text-slate-500 py-4">No digital study material for this filter yet.</div>}>
          {digital.map(c => (
            <div key={c.content_id} className="min-w-[210px] snap-start nb-card bg-white p-3" data-testid={`store-digital-${c.content_id}`}>
              <div className="h-24 bg-[#C4B5FD] border-2 border-black rounded grid place-items-center mb-2 overflow-hidden">
                {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" /> : <BookOpen size={26} />}
              </div>
              <div className="font-bold text-sm line-clamp-2">{c.title}</div>
              <div className="text-xs opacity-60">{c.board} {c.grade} · {c.subject}</div>
              <div className="nb-badge bg-[#86EFAC] text-[10px] mt-2 inline-block">{c.content_type}</div>
            </div>
          ))}
        </HRail>

        <StorePlans />

        {/* Shop from a store */}
        <section className="space-y-3">
          <h2 className="display text-lg font-bold">Shop from a store</h2>
          <div className="flex gap-2 flex-wrap">
            <select className="nb-select" value={vendorId} onChange={e => setVendorId(e.target.value)} data-testid="store-vendor-select">
              {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.business_name} · {v.city}</option>)}
              {vendors.length === 0 && <option value="">No stores yet</option>}
            </select>
            <select className="nb-select" value={cat} onChange={e => setCat(e.target.value)} data-testid="store-category-select">
              <option value="">All categories</option>
              {cats.map(c => <option key={c.category_code} value={c.category_code}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map(p => (
              <div key={p.product_id} className="nb-card bg-white p-4 flex flex-col" data-testid={`store-product-${p.product_id}`}>
                <div className="h-28 bg-[#FAF7F0] border-2 border-black rounded grid place-items-center mb-2 overflow-hidden">
                  {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <Package size={32} className="opacity-40" />}
                </div>
                <div className="font-bold text-sm flex-1">{p.title}</div>
                <div className="text-xs opacity-60">{p.category_code} {p.grade_tag && `· ${p.grade_tag}`}</div>
                <div className="display text-lg font-black mono mt-1">₹{p.selling_price}{p.mrp > p.selling_price && <span className="text-xs line-through opacity-50 ml-1">₹{p.mrp}</span>}</div>
                <button className="nb-btn text-xs mt-2 w-full justify-center" disabled={p.stock_qty <= 0} onClick={() => add(p)} data-testid={`store-add-${p.product_id}`}>
                  {p.stock_qty > 0 ? "Add to Cart" : "Out of stock"}
                </button>
              </div>
            ))}
            {products.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Pick a store to browse its catalog.</div>}
          </div>
        </section>
      </main>

      {checkoutOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setCheckoutOpen(false)}>
          <div className="nb-card bg-white p-5 max-w-md w-full space-y-3" onClick={e => e.stopPropagation()} data-testid="store-checkout-modal">
            <h3 className="display text-lg font-bold">Guest Checkout · {vendor?.business_name}</h3>
            {items.map(x => (
              <div key={x.product.product_id} className="flex justify-between text-sm">
                <span>{x.product.title} × {x.qty}</span>
                <span className="flex items-center gap-2 mono">₹{(x.product.selling_price * x.qty).toFixed(2)}
                  <button className="text-red-600 text-xs" onClick={() => remove(x.product.product_id)}>✕</button></span>
              </div>
            ))}
            <div className="flex justify-between font-bold border-t-2 border-black pt-2 mono">Total <span data-testid="store-checkout-total">₹{total.toFixed(2)}</span></div>
            <input className="nb-input" placeholder="Full Name *" value={cust.customer_name} onChange={e => setCust({ ...cust, customer_name: e.target.value })} data-testid="store-cust-name" />
            <input className="nb-input mono" placeholder="Phone *" value={cust.customer_phone} onChange={e => setCust({ ...cust, customer_phone: e.target.value })} data-testid="store-cust-phone" />
            <textarea className="nb-input" rows="2" placeholder="Delivery Address" value={cust.delivery_address} onChange={e => setCust({ ...cust, delivery_address: e.target.value })} data-testid="store-cust-address" />
            <button className="nb-btn w-full justify-center" onClick={checkout} disabled={busy} data-testid="store-pay-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : `Pay ₹${total.toFixed(2)}`}
            </button>
          </div>
        </div>
      )}

      {receipt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setReceipt(null)}>
          <div className="nb-card bg-[#86EFAC] p-6 max-w-md w-full space-y-3 text-center" onClick={e => e.stopPropagation()} data-testid="store-receipt">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-black grid place-items-center mx-auto"><Check size={28} /></div>
            <h3 className="display text-2xl font-black">Order Confirmed!</h3>
            <p className="text-sm">Show this 4-digit delivery PIN to the store on handover:</p>
            <div className="display text-5xl font-black mono tracking-widest bg-white border-2 border-black rounded p-3" data-testid="store-receipt-otp">{receipt.delivery_otp}</div>
            <div className="text-xs mono">Order {receipt.order_id.slice(0, 8)} · ₹{receipt.total} · {receipt.payment_status}</div>
            <button className="nb-btn w-full justify-center" onClick={() => setReceipt(null)}>Done</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function Storefront() {
  return (
    <PublicLayout theme="light">
      <StoreInner />
    </PublicLayout>
  );
}
